import { useState, useRef, useImperativeHandle, forwardRef, useEffect, useMemo, useCallback } from "react";
import { Table, Radio } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as XLSX from "xlsx";
import ServiceExcelSale from "./service-excel-sale";
import "./service-excel.css";

dayjs.extend(customParseFormat);

const parseImportDate = (value) => {
  if (value === null || value === undefined || value === "") return dayjs("");
  if (typeof value === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    return dayjs(value, "DD.MM.YYYY", true);
  }
  return dayjs(value);
};

const formatImportDate = (value) => {
  const date = parseImportDate(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : null;
};

const importNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  return Number(typeof value === "string" ? value.replace(/\s/g, "").replace(",", ".").replace(/%$/, "") : value);
};

const calculateDebt = (amount, payment) =>
  Number((importNumber(amount) - importNumber(payment)).toFixed(12));

const readDiscount = (value, cell) => {
  if (value === null || value === undefined || value === "") return null;
  // Excel stores 10% as 0.1; the sale expects percentage points.
  if (typeof value === "number" && (cell?.w?.includes("%") || cell?.z?.includes("%"))) {
    return Number((value * 100).toFixed(12));
  }
  const number = importNumber(value);
  return Number.isFinite(number) ? number : value;
};

const ServiceExcelSales = forwardRef((props, ref) => {
  const { onSalesChange, clientsRef, productsRef } = props || {};
  const [sales, setSales] = useState([]);
  const [validationErrors, setValidationErrors] = useState({}); // { "recordKey-fieldName": "error" | "warning" }
  const [editingRecord, setEditingRecord] = useState(null); // Запись, которая редактируется
  const [filterType, setFilterType] = useState("all"); // "all" | "errors" | "warnings"
  const salesRef = useRef(null);
  const { clientsCount = 0, productsCount = 0 } = props || {};
  
  useEffect(() => {
    if (!sales.length) {
      setValidationErrors({});
      return;
    }
    const allClients  = clientsRef?.current?.getClients?.()  || [];
    const allProducts = productsRef?.current?.getProducts?.() || [];

    const errors = {};
    sales.forEach((record) => {
      Object.assign(errors, validateRecord(record, sales, allClients, allProducts));
    });
    setValidationErrors(errors);
  }, [sales, clientsCount, productsCount, clientsRef, productsRef]);

  // Маппинг колонок Excel на поля таблиц
  const salesColumnMap = {
    "Внешний ID": "externalId",
    "Дата продажи": "saleDate",
    "Дата активации": "activationDate",
    "Дата окончания": "finishDate",
    "Сумма": "amount",
    "Оплата": "payment",
    "Скидка": "discountPrc",
    "Комментарий": "comment",
    "Внешний ID клиента": "clientExternalId",
    "Внешний ID абонемента": "abonementExternalId"
  };

  // Валидация для таблицы Абонементы клиентов
  const validateSaleField = (fieldName, value, record, allSales, allProducts) => {
    const isEmpty = value === null || value === undefined || value === "";
    if (!isEmpty) {
      const numericValue = importNumber(value);
      if (fieldName === "comment" && !["string", "number", "boolean"].includes(typeof value)) return "error";
      if (["amount", "payment", "debt", "discountPrc"].includes(fieldName) &&
          (typeof numericValue !== "number" || !Number.isFinite(numericValue))) return "error";
    }

    const isValidIsoDate = (value) => parseImportDate(value).isValid();

    switch (fieldName) {
      case "externalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Проверка на дубли
        const duplicateExternalId = allSales.filter(
          (a) => a.key !== record.key && a.externalId === value
        );
        if (duplicateExternalId.length > 0) return "error"; // Критическая ошибка
        return null;

      case "saleDate":
        if (isEmpty) return "error"; // Критическая ошибка
        if (!isValidIsoDate(value)) return "error";
        return null;

      case "activationDate":
        if (isEmpty) return "warning"; // Новый абонемент может не иметь даты
        if (!isValidIsoDate(value)) return "error";
        return null;
      
      case "finishDate":
        if (isEmpty) return "warning"; // Новый абонемент может не иметь даты
        if (!isValidIsoDate(value)) return "error";
        return null;

      case "amount":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value <= 0) return "warning"; // Предупреждение
        return null;

      case "payment":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "debt":
        return calculateDebt(record.amount, record.payment) < 0 ? "error" : null;

      case "discountPrc":
        if (isEmpty) return "warning"; // Предупреждение
        if (value !== null && value !== undefined && value < 0) return "warning"; // Предупреждение
        return null;

      case "comment":
        // Нет правил валидации
        return null;

      case "clientExternalId":
        if (isEmpty) return "error"; // Критическая ошибка
        // Получение значений таблицы Клиенты из рефа
        const allClients = clientsRef?.current?.getClients() || [];
        // Проверка наличия значения в таблице Клиенты (сравнение как числа и строки)
        const clientExists = allClients && allClients.some((c) => {
          const clientId = c.externalId;
          return clientId === value || String(clientId) === String(value) || Number(clientId) === Number(value);
        });
        if (!clientExists) return "error"; // Критическая ошибка
        return null;

      case "abonementExternalId":
        if (isEmpty) return "error";
        if (Number(value) > 0) return "error"; // Критическая ошибка
        // Проверка наличия значения в таблице Номенклатура (сравнение как числа и строки)
        allProducts = productsRef?.current?.getProducts?.() || [];
        const productExists = allProducts && allProducts.some((n) => {
          const nomId = n.externalId;
          return nomId === value || String(nomId) === String(value) || Number(nomId) === Number(value);
        });
        if (!productExists) return "error"; // Критическая ошибка
        return null;

      default:
        return null;
    }
  };

  // Функция для валидации всех полей записи
  const validateRecord = (record, allSales, allClients, allProducts) => {
    const errors = {};
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "debt", "discountPrc", "comment", "clientExternalId", "abonementExternalId"];
    
    fieldsToValidate.forEach(fieldName => {
      const validation = validateSaleField(fieldName, record[fieldName], record, allSales, allClients, allProducts);
      if (validation) {
        errors[`${record.key}-${fieldName}`] = validation;
      }
    });
    
    return errors;
  };

  const getCellClassName = useCallback((fieldName, record) => {
    const errorKey = `${record.key}-${fieldName}`;
    const validation = validationErrors[errorKey];
    
    let className = "service-excel-cell";
    if (validation === "error") {
      className += " service-excel-cell-error";
    } else if (validation === "warning") {
      className += " service-excel-cell-warning";
    }
    return className;
  }, [validationErrors]);

  const handleRowClick = useCallback((record) => {
    const editable = { ...record };
    // Если дата невалидная - при редактировании показываем пустое значение
    ["saleDate", "activationDate", "finishDate"].forEach((field) => {
      const val = editable[field];
      if (val !== null && val !== undefined && val !== "" && !parseImportDate(val).isValid()) {
        editable[field] = null;
      }
    });
    setEditingRecord(editable);
  }, []);

  const handleSave = (updatedRecord) => {
    updatedRecord = { ...updatedRecord, debt: calculateDebt(updatedRecord.amount, updatedRecord.payment) };
    setSales(prev => {
      // Проверяем, существует ли запись в массиве
      const existingIndex = prev.findIndex(item => item.key === updatedRecord.key);
      let newSales;
      
      if (existingIndex >= 0) {
        // Обновляем существующую запись
        newSales = prev.map(item => 
          item.key === updatedRecord.key ? updatedRecord : item
        );
      } else {
        // Добавляем новую запись
        newSales = [...prev, updatedRecord];
      }
      
      // Валидация после сохранения
      const allClients = clientsRef?.current?.getClients() || [];
      const allProducts = productsRef?.current?.getProducts() || [];
      const recordErrors = validateRecord(updatedRecord, newSales, allClients, allProducts);
      
      setValidationErrors(validationErrorsPrev => {
        const filtered = Object.keys(validationErrorsPrev)
          .filter(key => !key.startsWith(`${updatedRecord.key}-`))
          .reduce((obj, key) => {
            obj[key] = validationErrorsPrev[key];
            return obj;
          }, {});
        return { ...filtered, ...recordErrors };
      });
      
      if (onSalesChange) onSalesChange(newSales.length);
      return newSales;
    });
    setEditingRecord(null);
  };

  const handleCancel = () => {
    setEditingRecord(null);
  };


  const addRow = () => {
    const newKey = Date.now();
    const newRecord = {
      key: newKey,
      externalId: "",
      saleDate: null,
      amount: null,
      payment: null,
      discountPrc: null,
      comment: "",
      clientExternalId: "",
      abonementExternalId: ""
    };
    setEditingRecord(newRecord);
  };

  const deleteRow = useCallback((record) => {
    setSales(prev => {
      const newSales = prev.filter(item => item.key !== record.key);
      if (onSalesChange) onSalesChange(newSales.length);
      return newSales;
    });
    // Удаляем все ключи валидации для удаленной записи
    setValidationErrors(prev => {
      const filtered = Object.keys(prev)
        .filter(key => !key.startsWith(`${record.key}-`))
        .reduce((obj, key) => {
          obj[key] = prev[key];
          return obj;
        }, {});
      return filtered;
    });
  }, [onSalesChange]);

  const clearSales = () => {
    setSales([]);
    setValidationErrors({});
    if (onSalesChange) onSalesChange(0);
  };

  const parseSheetData = (worksheet) => {
    if (!worksheet) return [];
    
    // Используем raw: false для правильной обработки дат
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1, 
      defval: "",
      raw: true,
      cellDates: true,
      dateNF: "dd.mm.yyyy"
    });
    if (jsonData.length < 2) return []; // Нет заголовков или данных

    const headers = jsonData[0].map(h => String(h || "").trim());
    const rows = jsonData.slice(1);
    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    // Определяем поля, которые должны быть датами
    const dateFields = ["saleDate", "activationDate", "finishDate"];

    return rows.map((row, index) => {
      // Проверяем первую колонку - если пустая, пропускаем запись
      const firstColumnValue = row[0];
      if (firstColumnValue === "" || firstColumnValue === null || firstColumnValue === undefined) {
        return null; // Возвращаем null для фильтрации
      }
      
      const record = { key: `sale-${Date.now()}-${index}-${Math.random()}` };
      headers.forEach((header, colIndex) => {
        // Ищем поле с учетом возможных различий в регистре и пробелах
        let fieldName = salesColumnMap[header];
        if (!fieldName) {
          const matchedKey = Object.keys(salesColumnMap).find(key => 
            key.toLowerCase().trim() === header.toLowerCase().trim()
          );
          if (matchedKey) {
            fieldName = salesColumnMap[matchedKey];
          }
        }
        
        if (fieldName) {
          let value = row[colIndex];
          if (fieldName === "discountPrc") {
            const cell = worksheet[XLSX.utils.encode_cell({ r: range.s.r + index + 1, c: range.s.c + colIndex })];
            value = readDiscount(value, cell);
          }
          
          // Обработка пустых значений
          if (value === "" || value === null || value === undefined) {
            value = null;
          } 
          // Обработка дат
          else if (dateFields.includes(fieldName)) {
            if (typeof value === "number") {
              const excelEpoch = new Date(1899, 11, 30);
              const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
              value = date.toISOString();
            } else if (typeof value === "string") {
              const trimmedValue = value.trim();

              // Строгий формат DD.MM.YYYY
              const parsed = dayjs(trimmedValue, "DD.MM.YYYY", true);

              if (parsed.isValid()) {
                value = parsed.format("DD.MM.YYYY");
              } else {
                value = trimmedValue;
              }
            }
          }
          // Обработка чисел
          else if (typeof value === "number") {
            value = value;
          } 
          // Обработка строковых значений
          else {
            value = String(value).trim();
            
            // Пытаемся преобразовать строку в число, если поле числовое
            const numericFields = [
              "amount", "payment", "debt", "discountPrc",
              "clientExternalId", "abonementExternalId"
            ];
            if (numericFields.includes(fieldName) && value !== "") {
              const numValue = importNumber(value);
              value = isNaN(numValue) ? value : numValue;
            }
          }
          
          record[fieldName] = value;
        }
      });
      record.debt = calculateDebt(record.amount, record.payment);
      return record;
    }).filter(record => {
      // Фильтруем null записи (где первая колонка была пустая)
      if (record === null) return false;
      // Фильтруем пустые строки - проверяем, есть ли хотя бы одно непустое значение
      const values = Object.values(record).filter(key => key !== "key");
      return values.some(value => value !== null && value !== "" && value !== undefined);
    });
  };

  const loadFromExcel = (worksheet) => {
    const salesData = parseSheetData(worksheet);
    setSales(salesData);
    setEditingRecord(null);
    return salesData.length;
  };

  // Синхронизация количества продаж с родительским компонентом
  useEffect(() => {
    if (onSalesChange) {
      onSalesChange(sales.length);
    }
  }, [sales, onSalesChange]);

  // Проверка наличия критических ошибок
  const hasCriticalErrors = () => {
    const allClients = clientsRef?.current?.getClients() || [];
    const allProducts = productsRef?.current?.getProducts() || [];
    return sales.some(record =>
      Object.values(validateRecord(record, sales, allClients, allProducts)).includes("error")
    );
  };

  // Получение данных для сохранения
  const getSalesData = () => {
    const parseFloat = (value) => {
      const number = importNumber(value);
      return Number.isFinite(number) ? number : 0;
    };
    const parseInt = (value, defaultValue = 0) => {
      const number = Number.parseInt(value, 10);
      return Number.isFinite(number) ? number : defaultValue;
    };

    const formatDate = formatImportDate;

    return sales.map(sale => ({
      Id: parseInt(sale.externalId),
      CompanyId: 0,
      CreatedOn: formatDate(sale.saleDate) || dayjs().format("YYYY-MM-DD"),
      DocDate: formatDate(sale.saleDate) || dayjs().format("YYYY-MM-DD"),
      Activation: formatDate(sale.activationDate),
      Finish: formatDate(sale.finishDate),
      Syma: parseFloat(sale.amount),
      Paid: parseFloat(sale.payment),
      DiscountPrc: parseFloat(sale.discountPrc),
      Comment: String(sale.comment ?? ""),
      ClientId: parseInt(sale.clientExternalId),
      ProductId: parseInt(sale.abonementExternalId),
      Status: 1
    }));
  };

  // Проверка наличия ошибок у записи
  const hasRecordError = useCallback((record) => {
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "debt", "discountPrc", "comment", "clientExternalId", "abonementExternalId"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "error") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Проверка наличия предупреждений у записи
  const hasRecordWarning = useCallback((record) => {
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "debt", "discountPrc", "comment", "clientExternalId", "abonementExternalId"];
    const recordKey = String(record.key);
    
    for (const field of fieldsToValidate) {
      const errorKey = `${recordKey}-${field}`;
      if (validationErrors[errorKey] === "warning") {
        return true;
      }
    }
    return false;
  }, [validationErrors]);

  // Подсчет записей с ошибками и предупреждениями
  const getValidationCounts = () => {
    const errorKeys = new Set();
    const warningKeys = new Set();
    
    // Получаем множество существующих ключей записей (как строки для сравнения)
    const existingKeys = new Set(sales.map(s => String(s.key)));
    
    // Получаем список всех полей для валидации
    const fieldsToValidate = ["externalId", "saleDate", "activationDate", "finishDate", "amount", "payment", "debt", "discountPrc", "comment", "clientExternalId", "abonementExternalId"];
    
    // Сначала собираем все ошибки и предупреждения по записям
    Object.entries(validationErrors).forEach(([key, type]) => {
      // Извлекаем ключ записи - находим последнее поле из списка полей в ключе
      let recordKey = null;
      for (const field of fieldsToValidate) {
        if (key.endsWith(`-${field}`)) {
          recordKey = key.slice(0, -(field.length + 1)); // Убираем "-fieldName" с конца
          break;
        }
      }
      
      // Если не нашли через поля, используем старый метод как fallback
      if (!recordKey) {
        const parts = key.split('-');
        if (parts.length > 1) {
          // Берем все части кроме последней (которая является fieldName)
          recordKey = parts.slice(0, -1).join('-');
        } else {
          recordKey = key;
        }
      }
      
      // Учитываем только ошибки для существующих записей
      if (recordKey && existingKeys.has(recordKey)) {
        if (type === "error") {
          errorKeys.add(recordKey);
        } else if (type === "warning") {
          warningKeys.add(recordKey);
        }
      }
    });
    
    return {
      errors: errorKeys.size,
      warnings: warningKeys.size
    };
  };

  // Предоставляем методы через ref
  useImperativeHandle(ref, () => ({
    clearSales,
    loadFromExcel,
    hasCriticalErrors,
    getSalesData,
    getSales: () => sales,
    addRow,
    getValidationCounts
  }));

  const getSalesColumns = useMemo(() => [
    {
      title: "",
      width: 60,
      align: "center",
      render: (_, record) => (
        <img
          src="/images/trashcan.svg"
          alt="delete"
          onClick={(e) => {
            e.stopPropagation();
            deleteRow(record);
          }}
          className="img-edit-btn service-excel-action-icon"
        />
      ),
    },
    {
      title: "Внешний ID",
      dataIndex: "externalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("externalId", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Дата продажи",
      dataIndex: "saleDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("saleDate", record);
        let displayValue = "";
        if (record.saleDate) {
          const parsed = parseImportDate(record.saleDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          } else {
            displayValue = String(record.saleDate);
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Дата активации",
      dataIndex: "activationDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("activationDate", record);
        let displayValue = "";
        if (record.activationDate) {
          const parsed = parseImportDate(record.activationDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          } else {
            displayValue = String(record.activationDate);
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Дата окончания",
      dataIndex: "finishDate",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("finishDate", record);
        let displayValue = "";
        if (record.finishDate) {
          const parsed = parseImportDate(record.finishDate);
          if (parsed.isValid()) {
            displayValue = parsed.format("DD.MM.YYYY");
          } else {
            displayValue = String(record.finishDate);
          }
        }
        return <div className={className}>{displayValue}</div>;
      },
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("amount", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Оплата",
      dataIndex: "payment",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("payment", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Долг",
      dataIndex: "debt",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("debt", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Скидка",
      dataIndex: "discountPrc",
      width: 120,
      render: (text, record) => {
        const className = getCellClassName("discountPrc", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Комментарий",
      dataIndex: "comment",
      width: 200,
      render: (text, record) => {
        const className = getCellClassName("comment", record);
        return <div className={className}>{text || ""}</div>;
      },
    },
    {
      title: "Внешний ID клиента",
      dataIndex: "clientExternalId",
      width: 150,
      render: (text, record) => {
        const className = getCellClassName("clientExternalId", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
    {
      title: "Внешний ID абонемента",
      dataIndex: "abonementExternalId",
      width: 180,
      render: (text, record) => {
        const className = getCellClassName("abonementExternalId", record) + " service-excel-cell-right";
        return <div className={className}>{text !== null && text !== undefined ? text : ""}</div>;
      },
    },
  ], [deleteRow, getCellClassName]);

  const getTableHeight = (ref) => {
    if (!ref.current) return 400;
    const headerSize = ref.current.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
    return ref.current ? (document.documentElement.offsetHeight - headerSize - ref.current.getBoundingClientRect().top - window.scrollY - 150) : 400;
  };

  // Компоненты для таблиц с убранным padding в body ячейках
  const tableComponents = {
    body: {
      cell: (props) => {
        const { children, ...restProps } = props;
        return (
          <td {...restProps} style={{ ...restProps.style, padding: 0 }}>
            {children}
          </td>
        );
      },
    },
  };

  // Фильтрация данных в зависимости от выбранного фильтра
  const filteredSales = useMemo(() => {
    if (filterType === "all") {
      return sales;
    } else if (filterType === "errors") {
      return sales.filter(sale => hasRecordError(sale));
    } else if (filterType === "warnings") {
      return sales.filter(sale => hasRecordWarning(sale));
    }
    return sales;
  }, [sales, filterType, hasRecordError, hasRecordWarning]);

  // Получаем счетчики для отображения
  const validationCounts = getValidationCounts();

  return (
    <>
      <div ref={salesRef}>
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <Radio.Group 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Всего ({sales.length})
            </Radio.Button>
            <Radio.Button value="errors">
              Ошибок ({validationCounts.errors})
            </Radio.Button>
            <Radio.Button value="warnings">
              Предупреждений ({validationCounts.warnings})
            </Radio.Button>
          </Radio.Group>
        </div>
        <Table
          dataSource={filteredSales}
          columns={getSalesColumns}
          rowKey="key"
          size="small"
          pagination={false}
          scroll={{ x: "max-content", y: getTableHeight(salesRef) }}
          bordered
          components={tableComponents}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" }
          })}
        />
      </div>
      {editingRecord && (
        <ServiceExcelSale
          record={editingRecord}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
    </>
  );
});

ServiceExcelSales.displayName = "ServiceExcelSales";

export default ServiceExcelSales;

